import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'

import { useMediaQuery } from '@/hooks/useBreakpoint'
import useTourStore from '@/components/Organizer/Tours/tourStore'
import { readPin, writePin } from './orgSide.storage'

/** The mock's `(max-width:1100px)`: above it a column, below a fixed rail with a tap-to-open panel. */
export const NARROW_QUERY = '(max-width: 1100px)'
const HOVER_QUERY = '(hover: hover)'
const HOVER_IN_MS = 80
const HOVER_OUT_MS = 150

/**
 * State machine of the organizer side menu (mock `_kit/orgside.html` script).
 *
 * > 1100: `pinned` (localStorage `sf-osd`) → column 240; otherwise a 64-px rail that unfolds as an
 * overlay while the pointer (80 ms in / 150 ms out, hover-capable pointers only) or the keyboard focus
 * is inside, a tap on touch screens too; Escape folds it back.
 * ≤ 1100: the rail is fixed. A hover-capable pointer unfolds the 280-px panel as an overlay the same
 * way (`hover`, body `osd-hover`: no scrim, the page keeps scrolling — the app is ahead of the mock
 * here, William 14 Oct 2026: a narrow desktop window still hovers); a tap `open`s it as a drawer over
 * a scrim. ×, the scrim, Escape and any navigation fold everything. No pin, no memory there.
 *
 * A guided tour (Tours/tourStore `menuHold`, 28 Oct 2026) holds the panel unfolded — the drawer open
 * ≤ 1100 — whatever the pointer does, so a step can point at a menu row; the pin is never written.
 *
 * Returns the derived visuals (`rail`, `unfolded`) and the handlers to spread on the aside.
 */
export default function useOrgSideState({ enabled = true } = {}) {
    const narrow = useMediaQuery(NARROW_QUERY)
    const canHover = useMediaQuery(HOVER_QUERY)
    const { pathname } = useLocation()
    const held = useTourStore((state) => !!state.menuHold)

    const [pinned, setPinnedState] = useState(readPin)
    const [hover, setHoverState] = useState(false)
    const [focusIn, setFocusIn] = useState(false)
    const [open, setOpen] = useState(false)

    const asideRef = useRef(null)
    const hoverTimer = useRef(0)
    const prevFocus = useRef(null)

    const clearHoverTimer = () => {
        clearTimeout(hoverTimer.current)
        hoverTimer.current = 0
    }

    const setHover = useCallback((on) => {
        clearHoverTimer()
        setHoverState(on)
    }, [])

    // 80 ms in, 150 ms out: no flash when the pointer merely crosses the rail (a stored pin only
    // matters above 1100: below, the column is a rail whatever the storage says)
    const wantHover = useCallback(
        (on) => {
            if (held) return
            if (!narrow && pinned) return
            clearHoverTimer()
            hoverTimer.current = setTimeout(() => setHoverState(on), on ? HOVER_IN_MS : HOVER_OUT_MS)
        },
        [narrow, pinned, held],
    )

    const setPinned = useCallback((on) => {
        setPinnedState(on)
        writePin(on)
    }, [])

    const openPanel = useCallback(() => {
        setOpen((was) => {
            if (was) return was
            prevFocus.current = document.activeElement
            return true
        })
    }, [])

    const closePanel = useCallback(() => {
        setOpen((was) => {
            if (!was) return was
            const previous = prevFocus.current
            prevFocus.current = null
            if (previous && previous.isConnected) previous.focus({ preventScroll: true })
            return false
        })
    }, [])

    /** Folds everything: the tap drawer, the hover overlay and the keyboard unfold. */
    const fold = useCallback(() => {
        closePanel()
        setHover(false)
        setFocusIn(false)
    }, [closePanel, setHover])

    // focus the × once the drawer is open (≤ 1100, tap only: a hover never steals the focus)
    useEffect(() => {
        if (!open) return
        asideRef.current?.querySelector('.osd-x')?.focus({ preventScroll: true })
    }, [open])

    // a navigation closes the drawer / the hover overlay
    useEffect(() => {
        fold()
    }, [pathname, fold])

    // crossing 1100: the drawer closes, hover / focus reset
    useEffect(() => {
        fold()
    }, [narrow, fold])

    // Escape (capture, before the page's own Escape handlers); mousedown outside folds the overlay
    useEffect(() => {
        if (!enabled) return undefined
        const onKey = (event) => {
            if (event.key !== 'Escape' || held) return
            if (narrow && open) {
                event.stopPropagation()
                closePanel()
                return
            }
            if ((narrow || !pinned) && (hover || focusIn)) {
                event.stopPropagation()
                setHover(false)
                setFocusIn(false)
                if (asideRef.current?.contains(document.activeElement)) document.activeElement.blur()
            }
        }
        const onMouseDown = (event) => {
            if (hover && !asideRef.current?.contains(event.target)) setHover(false)
        }
        window.addEventListener('keydown', onKey, true)
        document.addEventListener('mousedown', onMouseDown)
        return () => {
            window.removeEventListener('keydown', onKey, true)
            document.removeEventListener('mousedown', onMouseDown)
        }
    }, [enabled, narrow, open, pinned, hover, focusIn, held, closePanel, setHover])

    useEffect(() => () => clearHoverTimer(), [])

    const rail = held ? false : narrow ? !open && !hover : !pinned && !hover && !focusIn
    const unfolded = held ? true : narrow ? open || hover : pinned || hover || focusIn

    // body classes (mock: `osd-on`, `osd-min` = 64-px column, `osd-rail` = rail visuals, `osd-open` = drawer;
    // app: `osd-hover` = the ≤ 1100 hover overlay, panel 280 over the fixed rail without a scrim)
    useLayoutEffect(() => {
        const { classList } = document.body
        if (!enabled) {
            classList.remove('osd-on', 'osd-min', 'osd-rail', 'osd-open', 'osd-hover')
            return undefined
        }
        classList.add('osd-on')
        classList.toggle('osd-min', narrow || !pinned)
        classList.toggle('osd-rail', rail)
        classList.toggle('osd-open', narrow && (open || held))
        classList.toggle('osd-hover', narrow && hover && !open && !held)
        return () => classList.remove('osd-on', 'osd-min', 'osd-rail', 'osd-open', 'osd-hover')
    }, [enabled, narrow, pinned, rail, open, hover, held])

    const handlers = {
        onMouseEnter: () => {
            if (canHover) wantHover(true)
        },
        onMouseLeave: () => {
            if (canHover) wantHover(false)
        },
        onFocus: () => {
            if (!narrow) setFocusIn(true)
        },
        onBlur: (event) => {
            if (!asideRef.current?.contains(event.relatedTarget)) setFocusIn(false)
        },
    }

    /**
     * Click inside the aside, before the row's own handler: ≤ 1100 a tap on the closed rail only opens
     * the panel (links included: one reads the labels before navigating) — a panel already unfolded by
     * the pointer lets the click through; > 1100 on a touch screen a tap in the rail unfolds it. Returns
     * true when the click was consumed.
     */
    const onRailClick = (event) => {
        if (held) return false
        if (narrow) {
            if (open || hover) return false
            if (event.target.closest('a')) event.preventDefault()
            openPanel()
            return true
        }
        if (!canHover && !pinned && !hover) {
            setHover(true)
            return event.target.closest('a') ? false : true
        }
        return false
    }

    return {
        asideRef,
        narrow,
        pinned,
        rail,
        unfolded,
        open,
        held,
        handlers,
        onRailClick,
        setPinned,
        openPanel,
        closePanel,
        fold,
    }
}
